import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Zap, Target, Award, AlertTriangle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { bn } from 'date-fns/locale';

interface MatchEvent {
  id: string;
  match_id: string;
  event_type: string;
  minute: number;
  created_at: string;
  player?: { id: string; name: string; avatar: string | null };
  match?: { 
    score_a: number; 
    score_b: number;
    player_a: { name: string };
    player_b: { name: string };
  };
}

const EVENT_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  'goal': { icon: Target, color: 'text-green-500', label: 'গোল' },
  'assist': { icon: Zap, color: 'text-blue-500', label: 'এসিস্ট' },
  'yellow_card': { icon: AlertTriangle, color: 'text-yellow-500', label: 'হলুদ কার্ড' },
  'red_card': { icon: AlertTriangle, color: 'text-red-500', label: 'লাল কার্ড' },
  'motm': { icon: Award, color: 'text-yellow-400', label: 'ম্যান অফ দ্য ম্যাচ' },
};

export const MatchHighlightsFeed = () => {
  const [events, setEvents] = useState<MatchEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('match_events_feed')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'match_events'
      }, (payload) => {
        fetchEvents(); // Refetch to get full data with joins
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchEvents = async () => {
    const { data } = await supabase
      .from('match_events')
      .select(`
        id, match_id, event_type, minute, created_at,
        player:players!match_events_player_id_fkey(id, name, avatar),
        match:matches!match_events_match_id_fkey(
          score_a, score_b,
          player_a:players!matches_team_a_fkey(name),
          player_b:players!matches_team_b_fkey(name)
        )
      `)
      .in('event_type', ['goal', 'yellow_card', 'red_card'])
      .order('created_at', { ascending: false })
      .limit(20);

    if (data) setEvents(data as any);
    setLoading(false);
  };

  const getConfig = (type: string) => EVENT_CONFIG[type] || EVENT_CONFIG.goal;

  if (loading) {
    return (
      <Card className="p-4 animate-pulse">
        <div className="h-60 bg-muted rounded" />
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-primary animate-pulse" />
        <h3 className="font-bold text-lg">Match Highlights</h3>
        <Badge variant="secondary" className="ml-auto animate-pulse">
          Live Feed
        </Badge>
      </div>

      {events.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <Zap className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>কোন সাম্প্রতিক ইভেন্ট নেই</p>
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-3">
            {events.map((event, idx) => {
              const config = getConfig(event.event_type);
              const IconComponent = config.icon;
              const isNew = idx < 3;

              return (
                <div
                  key={event.id}
                  className={`relative flex items-start gap-3 p-3 rounded-lg transition-all
                    ${isNew ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30'}`}
                >
                  {/* Timeline dot */}
                  <div className={`shrink-0 p-2 rounded-full ${config.color.replace('text-', 'bg-')}/20`}>
                    <IconComponent className={`w-4 h-4 ${config.color}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {event.player && (
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={event.player.avatar || ''} />
                          <AvatarFallback className="text-xs">{event.player.name[0]}</AvatarFallback>
                        </Avatar>
                      )}
                      <span className="font-medium text-sm truncate">
                        {event.player?.name || 'Unknown'}
                      </span>
                      <Badge variant="outline" className={`${config.color} text-[10px] shrink-0`}>
                        {config.label}
                      </Badge>
                    </div>

                    {event.match && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {event.match.player_a?.name} {event.match.score_a} - {event.match.score_b} {event.match.player_b?.name}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{event.minute}'</span>
                      <span>•</span>
                      <span>{formatDistanceToNow(new Date(event.created_at), { addSuffix: true, locale: bn })}</span>
                    </div>
                  </div>

                  {/* New indicator */}
                  {isNew && (
                    <span className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full animate-pulse" />
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </Card>
  );
};
